import axios from "axios";
import { applyDevFakeError } from "./devFakeError";

// A body is the {success,data,error,meta} envelope (docs/api-response-contract.md)
// only if it carries all of `success`/`data`/`error` as real keys.
function isEnvelope(body) {
  return (
    body !== null &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    typeof body.success === "boolean" &&
    Object.prototype.hasOwnProperty.call(body, "data") &&
    Object.prototype.hasOwnProperty.call(body, "error")
  );
}

function headerRequestId(response) {
  return (response && response.headers && response.headers["x-request-id"]) || null;
}

// Copies an envelope's error onto a thrown error as
// code/message/details/requestId, and marks it `isApiError` so callers (and
// api/apiError.js) can tell a contract error code from one of axios's own
// transport codes (`ECONNABORTED`, `ERR_NETWORK`, ...), which share
// `error.code`.
function applyEnvelopeError(error, body, response) {
  const envelopeError = body.error || {};
  error.isApiError = true;
  error.code = envelopeError.code || null;
  error.message = envelopeError.message || error.message;
  error.details =
    envelopeError.details !== undefined ? envelopeError.details : null;
  error.requestId =
    envelopeError.request_id ||
    (body.meta && body.meta.request_id) ||
    headerRequestId(response);
  return error;
}

// The Node server answers every route the client calls with the envelope,
// so a 2xx body that isn't one (or a 2xx envelope carrying success: false)
// is a contract violation, not a legacy shape to pass along. It's rejected
// with the client-side code `invalid_response` instead of handing callers a
// body they'd misread.
function invalidResponseError(response) {
  const body = response.data;
  const error = new Error("Unexpected response from the server.");
  error.response = response;
  error.config = response.config;
  if (isEnvelope(body)) {
    return applyEnvelopeError(error, body, response);
  }
  error.isApiError = true;
  error.code = "invalid_response";
  error.details = null;
  error.requestId = headerRequestId(response);
  return error;
}

// axiosAuth (Node server): strict. Unwraps `response.data` to the envelope's
// `data` and exposes its `meta` on `response.meta`.
export function unwrapEnvelope(response) {
  const body = response.data;
  if (!isEnvelope(body) || body.success !== true) {
    return Promise.reject(invalidResponseError(response));
  }
  response.data = body.data;
  response.meta = body.meta || {};
  return response;
}

// axiosNoAuth (Connector App): tolerant. 4.x connectors return the
// envelope; pre-4.0 connectors still in the field return bare bodies, which
// pass through untouched. Retired with legacy connector support
// (docs/plans/connector-app-modernization.md, Ticket 8).
export function unwrapEnvelopeOrLegacy(response) {
  const body = response.data;
  if (isEnvelope(body) && body.success === true) {
    response.data = body.data;
    response.meta = body.meta || {};
  }
  return response;
}

// Normalizes an envelope error onto the thrown axios error. Mutates and
// rejects with the original error (rather than a new one) so
// `error.response.status` checks keep working. A non-envelope error body
// (an ALB error page, a network failure, a legacy connector) is left as
// axios built it, apart from picking up the x-request-id header if the
// response carried one.
export function normalizeEnvelopeError(error) {
  const response = error.response;
  const body = response && response.data;

  if (isEnvelope(body) && body.success === false) {
    applyEnvelopeError(error, body, response);
  } else if (response) {
    error.requestId = headerRequestId(response);
  }

  return Promise.reject(error);
}

// For API routes that require credentials (cookies/auth)
export const axiosAuth = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// For connector or public routes that do NOT require credentials
export const axiosNoAuth = axios.create({
  baseURL: process.env.REACT_APP_CONNECTOR_ROUTE,
  withCredentials: false,
});

axiosAuth.interceptors.response.use(unwrapEnvelope, normalizeEnvelopeError);
axiosNoAuth.interceptors.response.use(
  unwrapEnvelopeOrLegacy,
  normalizeEnvelopeError,
);

// DEV ONLY: ?fakeError=<path> simulates an error response (devFakeError.js).
applyDevFakeError(axiosAuth);
applyDevFakeError(axiosNoAuth);
